import { CloseOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import { getCompanyList } from "src/apis/companyApi";
import { getMenuList } from "src/apis/menuApi";
import { getMyInfo } from "src/apis/userApi";
import { MinWidthWrapper } from "src/components/common/common";
import SheetModal from "src/components/common/form/SheetModal";
import { AddressModal } from "src/components/common/modal/addressModal/AddressModal";
import { CodeSearchModal } from "src/components/common/modal/codeSearchModal/CodeSearchModal";
import Sidebar from "src/components/common/sidebar/Sidebar";
import SpinnerOverlay from "src/components/common/SpinnerOverlay";
import Topbar from "src/components/common/topbar/Topbar";
import FixedPageContainer from "src/containers/homepage/home/FixedPage";
import { SYS1010_R01Modal } from "src/containers/mes/SYS/SYS1010_R01/Modal_SYS1010_R01";
import { MenuDto } from "src/dtos/menu/menuDto";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useMenuStore, { defaultMenu } from "src/store/useMenuStore";
import useScreenStore from "src/store/useScreenStore";
import useUiuxStore from "src/store/useUiuxStore";
import useUserStore from "src/store/useUserStore";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { getMenuComponent } from "src/utils/menu/menuUtil";
import { TabUtils } from "src/utils/menu/tabUtils";
import { Content_Home } from "./HomeContext";
import { OrganizaMapModal } from "src/components/common/modal/organizaModal/OrganizaMapModal";
import { AuthactionbarLog } from "src/components/common/actionbar/AuthactionbarLog";
import { validateTokensForAppStart } from "src/utils/auth/tokenValidator";
import {
	redirectToSignin,
	showSessionErrorModal,
} from "src/utils/auth/sessionErrorHandler";
import { useCompanyStore } from 'src/store/useCompanyStore';
import { useQzTray } from 'src/hooks/useQzTray';
import ConfirmModal from "src/components/common/modal/ConfirmModal";

const HomeContainer = () => {
	// QZ Tray 앱 시작 시 자동 연결
	useQzTray();

	const {
		setScreenWidth,
		setScreenHeight,
		tabDivFloorHeight, setTabDivFloorHeight,
		tabDivWidth, setTabDivWidth,
		tabDivHeight, setTabDivHeight,
		tabWidth, setTabWidth,
		tabHeight, setTabHeight
	} = useScreenStore();

	const { 
		codeSearchModalVisible, commonCodeModalVisible, sidebarVisible, addressModalVisible, organizaMapModalVisible, sheetModalVisible,
		tabList, activeTab, addTabList, setActiveTab, setSidebarVisible, removeTabList, authactionbarLogModalVisible,
		confirmModalVisible, setConfirmModalVisible,
		confirmModalTrigger, setConfirmModalTrigger,
		tabContextMenu, closeTabContextMenu, openTabContextMenu, getTabContextMenuItems,
	} = useUiuxStore();
	const { selectedThirdMenu, menuList, setMenuList, setSelectedThirdMenu, setThirdMenuList, setSecondMenuList, setFourthMenuList, setFifthMenuList } = useMenuStore();


	const { setUserInfo, setUserInCompanyList, initData: initUserData } = useUserStore();
	const { companyList, setCompanyList, initData: initCompanyData } = useCompanyStore();
	const { loadingSpinner, setLoadingSpinner } = useLoadingSpinnerStore();

	const { handleExitClick } = TabUtils();
	const tabDivRef = useRef<HTMLDivElement>(null);

	// 1. 메뉴에 표시될 아이템들을 스토어에서 가져옵니다.
	const contextMenuItems = getTabContextMenuItems();

	// 메뉴 필터링 (API가 이미 트리 구조로 제공됨)
	const filteredMenuList = useMemo(() => {
		if (!menuList.length) return [];
		return menuList.filter(menu => menu.useYn === 'Y');
	}, [menuList]);

	const { 
		handleResize,
		refreshMesInfo,
	} = Content_Home({ 
		tabList,
		tabDivRef,  
	});

	useEffect(() => {
		// 앱 시작 시 토큰 검증 
		const tokenValidation = validateTokensForAppStart();

		if (!tokenValidation.isValid) {
			if (tokenValidation.reason === 'refresh-expired') {
				showSessionErrorModal('세션이 만료되었습니다. 다시 로그인해주세요.');
			} else {
				redirectToSignin();
			}
			return;
		}
		refreshMesInfo();
	}, []);

	useEffect(() => {
		if (filteredMenuList.length > 0) {
			const secondMenuList: MenuDto[] = [];
			const thirdMenuList: MenuDto[] = [];
			const fourthMenuList: MenuDto[] = [];
			const fifthMenuList: MenuDto[] = [];

			for (const menu of filteredMenuList) {
				for (const secondMenu of menu.subMenu) {
					secondMenuList.push(secondMenu);
					for (const thirdMenu of secondMenu.subMenu) {
						thirdMenuList.push(thirdMenu);
						for (const fourthMenu of thirdMenu.subMenu) {
							fourthMenuList.push(fourthMenu);
							for (const fifthMenu of fourthMenu.subMenu) {
								fifthMenuList.push(fifthMenu);
							}
						}
					}
				}
			}
			setSecondMenuList(secondMenuList);
			setThirdMenuList(thirdMenuList);
			setFourthMenuList(fourthMenuList);
			setFifthMenuList(fifthMenuList);
		} else {
			setSecondMenuList([]);
			setThirdMenuList([]);
			setFourthMenuList([]);
			setFifthMenuList([]);
		}
	}, [filteredMenuList]);

	useEffect(() => {
		if (!selectedThirdMenu && filteredMenuList.length > 0) {
			let initMenu: MenuDto | undefined = undefined;
			for (const menu of filteredMenuList) {
				for (const secondMenu of menu.subMenu) {
					for (const thirdMenu of secondMenu.subMenu) {
						if (thirdMenu.menuCd === defaultMenu) {
							initMenu = thirdMenu;
							break;
						}
					}
					if (initMenu) break;
				}
				if (initMenu) break;
			}
			setSelectedThirdMenu(initMenu);
			if (initMenu) {
				addTabList({
					key: initMenu.menuCd,
					title: initMenu.menuNm,
					component: getMenuComponent(initMenu.menuCd)
				});
				setActiveTab({
					key: initMenu.menuCd,
					title: initMenu.menuNm,
					component: getMenuComponent(initMenu.menuCd)
				});
			}
		}
	}, [filteredMenuList, selectedThirdMenu]);

	useEffect(() => {
		if (tabList.length === 0) {
			const fixedTab = {
				key: 'FIXED_TAB',
				title: '대시보드',
				component: FixedPageContainer,
				fixed: true
			};
			addTabList(fixedTab);
			setActiveTab(fixedTab);
		}
	}, []);

	//화면 크기 변경 시 화면 크기 저장
	useEffect(() => {
		const handleResize = () => {
			setScreenWidth(window.innerWidth);
			setScreenHeight(window.innerHeight);
		};

		window.addEventListener('resize', handleResize);
		handleResize();

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [setScreenWidth, setScreenHeight]);

	//화면 크기 변경 시 화면 크기 저장
	useEffect(() => {

		window.addEventListener('resize', handleResize);
		handleResize();
		
		// console.log('tabList :', JSON.stringify(tabList));
		//탭 추가 예시
		// const newTab = {
		// 	key: 'SYS1010_R01',
		// 	title: '시스템관리',
		// 	component: getMenuComponent('SYS1010_R01')
		// };
		// addTabList(newTab);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [setScreenWidth, setScreenHeight, tabList, tabWidth]);

	useEffect(() => {
		if (tabContextMenu.visible) {
			const handleClick = () => closeTabContextMenu();
			document.addEventListener('click', handleClick);
			return () => document.removeEventListener('click', handleClick);
		}
	}, [tabContextMenu.visible, closeTabContextMenu]);

	useEffect(() => {
		if (confirmModalTrigger) {
			setConfirmModalVisible(true);
			setConfirmModalTrigger(false);
		}
	}, [confirmModalTrigger]);

	return (
		<>
			<div className={`w-full ${MinWidthWrapper} h-full flex flex-col overflow-y-auto`}>
				<Topbar />
				{loadingSpinner && <SpinnerOverlay text="처리 중입니다..." />}
				<div className="flex-1 flex overflow-y-auto">
					{sidebarVisible && <div className="flex-shrink-0 h-full" style={{height: '100%'}}><Sidebar /></div>}
					<div className="flex flex-col flex-1 h-full">
						<div 
							className={`flex flex-wrap w-full min-w-[${tabDivWidth}px] h-[${tabDivHeight}px] items-end pl-[8px] pt-[4px]`}
							id='tabdiv'
							ref={tabDivRef}
							// 3. 탭 컨테이너에 스토어의 onContextMenu 핸들러를 연결합니다.
							onContextMenu={openTabContextMenu}
							>
							{ tabList.map(tab => 
									<div key={tab.key} className={`w-[${tabWidth}px] h-[${tabHeight}px] flex items-center justify-between px-[8px] cursor-pointer border
										${activeTab?.key === tab.key ? 'border-b-0 bg-blue-lightest border-t-blue' : 'bg-gray-100'}`}
										onClick={() => setActiveTab(tab)}>
										<p className="w-[120px] text-[12px] truncate">
											{tab.title}
										</p>
										{!tab.fixed && (
											// tab - x버튼 닫기
											<CloseOutlined className="text-gray-500 text-[12px]" onClick={(e) => { e.stopPropagation(); handleExitClick(tab.key); }} />
										)}
									</div>
								)
							}
						</div>
						<div className="flex flex-1 overflow-x-auto ">
							{activeTab?.component && <activeTab.component />}
						</div>
					</div>
				</div>
				{codeSearchModalVisible && <CodeSearchModal />}
				{commonCodeModalVisible && <SYS1010_R01Modal />}
				{addressModalVisible && <AddressModal />}
				{organizaMapModalVisible && <OrganizaMapModal />}
				{sheetModalVisible && <SheetModal />}
				{authactionbarLogModalVisible && <AuthactionbarLog />}
				{confirmModalVisible && <ConfirmModal />}
				
				{/* 컨텍스트 메뉴 UI를 렌더링합니다. */}
				{tabContextMenu.visible && (
					<>
						{/* 배경 클릭 시 메뉴를 닫기 위한 투명 오버레이 */}
						<div className="fixed inset-0 z-40" onClick={closeTabContextMenu} />
						<div
							className="fixed bg-blue-100 border border-gray-300 shadow-lg rounded-md py-1 z-50"
							style={{
								left: tabContextMenu.x,
								top: tabContextMenu.y,
								minWidth: '200px'
							}}
						>
							<p className="px-3 py-1 text-xs">{activeTab?.title}</p>
							<hr />
							{contextMenuItems.map((item, index) => (
								<div
									key={index}
									className={`px-3 py-2 font-size-8 bg-white ${
										item.disabled 
										? 'text-gray-400 cursor-not-allowed' 
										: 'hover:bg-gray-100 cursor-pointer'
									}`}
									onClick={() => {
										if (!item.disabled) {
											item.onClick();
											closeTabContextMenu();
										}
									}}
								>
									{item.label}
								</div>
							))}
						</div>
					</>
				)}
			</div>
		</>

	)
}

export default HomeContainer;
